import { Fragment, useCallback, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { ChevronRight, Eye, Info, Layers } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { DataListCardField } from "@/components/shared/DataListCardField";
import {
  groupProfessionalRowsBySourceRequest,
  toProfessionalListBlocks,
} from "@/utils/professionalRequestGrouping";
import { DataListCardRow } from "@/components/shared/DataListCardRow";
import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { FiscalDeclarationWarning } from "@/components/shared/FiscalDeclarationWarning";
import { PageHeader } from "@/components/shared/PageHeader";
import { ResponsiveListSplit } from "@/components/shared/ResponsiveListSplit";
import { QuoteDeadlineChip } from "@/components/features/notarial-requests/quote-deadline-chip";
import { StatusBadge, type StatusBadgeVariant } from "@/components/shared/StatusBadge";
import { FulfillmentStageChip } from "@/components/shared/FulfillmentStageChip";
import { TableCard } from "@/components/shared/TableCard";
import { TablePagination } from "@/components/shared/TablePagination";
import { TableActionsMenu, type TableAction } from "@/components/shared/TableActionsMenu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useProfessionalRequestCopy } from "@/hooks/useProfessionalRequestCopy";
import { usePaginationFromUrl } from "@/hooks/usePaginationFromUrl";
import { formatDisplayDate } from "@/lib/dateFormat";
import { formatUyMinor } from "@/lib/formatUyMinor";
import { useAuth } from "@/contexts/AuthContext";
import {
  listNotaryRequests,
  type MyQuoteStatus,
  type NotarialRequestListItem,
  type NotarialRequestStatus,
} from "@/services/notaryRequestsService";

const STATUS_OPTIONS: { value: NotarialRequestStatus | ""; labelPath: string }[] = [
  { value: "", labelPath: "requests.status" },
  { value: "pending", labelPath: "requests.statusPending" },
  { value: "in_progress", labelPath: "requests.statusInProgress" },
  { value: "completed", labelPath: "requests.statusCompleted" },
  { value: "missing_information", labelPath: "requests.statusMissingInfo" },
  { value: "cannot_complete", labelPath: "requests.statusCannotComplete" },
  {
    value: "document_ready_at_professional",
    labelPath: "requests.statusDocumentReadyAtProfessional",
  },
  { value: "document_at_platform", labelPath: "requests.statusDocumentAtPlatform" },
];

const SELECTABLE_STATUSES: NotarialRequestStatus[] = STATUS_OPTIONS
  .filter((o): o is { value: NotarialRequestStatus; labelPath: string } => o.value !== "")
  .map((o) => o.value);

const DEFAULT_STATUSES: NotarialRequestStatus[] = [
  "pending",
  "in_progress",
  "missing_information",
];

const REQUEST_TYPE_OPTIONS: { value: string; labelKey: string }[] = [
  { value: "notarial_certificate", labelKey: "notary.requests.requestType_notarial_certificate" },
  { value: "income_certificate", labelKey: "notary.requests.requestType_income_certificate" },
  { value: "notarial_legal_entity", labelKey: "notary.requests.requestType_notarial_legal_entity" },
  { value: "notarial_signature", labelKey: "notary.requests.requestType_notarial_signature" },
  { value: "notarial_address", labelKey: "notary.requests.requestType_notarial_address" },
];

const STATUS_VARIANTS: Record<NotarialRequestStatus, StatusBadgeVariant> = {
  // Defensive — professionals never see this status (the backend filter
  // excludes pending_profile_completion from their queue) but the Record
  // type requires every key. Same visual as `pending` if it ever leaks.
  pending_profile_completion: "warning",
  pending_payment_verification: "warning",
  pending: "warning",
  in_progress: "info",
  completed: "success",
  missing_information: "danger",
  cannot_complete: "softDanger",
  document_ready_at_professional: "warning",
  document_at_platform: "info",
  awaiting_consumer_delivery_details: "info",
  awaiting_shipment_to_consumer: "info",
  in_transit_to_consumer: "info",
};

/** A quote_request row's own status vocabulary doesn't exist in STATUS_VARIANTS/STATUS_OPTIONS above — this is the pro-facing view for it, keyed by the pro's OWN bid state on the row, not the request's internal state machine. */
const QUOTE_MY_STATUS_VARIANTS: Record<
  "available" | "missed" | MyQuoteStatus,
  StatusBadgeVariant
> = {
  // Derived, not served: an open row whose window has shut. Muted rather
  // than danger — nothing went wrong, the professional simply did not bid.
  missed: "softDanger",
  // Not `info`: that is what every in-progress trámite uses, so an open RFQ
  // looked identical to work the professional had already been assigned. This
  // is the only row in the list they can act on, and it should be the one that
  // catches the eye.
  available: "opportunity",
  submitted: "warning",
  withdrawn: "softDanger",
  awarded: "success",
  rejected: "softDanger",
  expired: "softDanger",
};

/** The detail page needs to know a row is a quote_request before it fetches — the id alone isn't enough to tell it apart from a trámite id. */
function detailUrlForRow(row: Pick<NotarialRequestListItem, "id" | "channel">): string {
  return row.channel === "quote" ? `/requests/${row.id}?channel=quote` : `/requests/${row.id}`;
}

/** A quote_request row carries its free-form title in `requestType` (see notary-requests.controller.ts's merge mapping) — every other row looks up a catalog i18n label instead. */
function typeLabelForRow(
  row: Pick<NotarialRequestListItem, "channel" | "requestType" | "sourceRequestTitle">,
  t: (key: string, options?: Record<string, unknown>) => string,
  nested = false,
): string {
  if (row.channel === "quote") return row.requestType;
  const label = t(`notary.requests.requestType_${row.requestType ?? "notarial_certificate"}`, {
    defaultValue: row.requestType ?? "notarial_certificate",
  });
  /*
    THE SUFFIX IS DROPPED UNDER A GROUP HEADER that already says it. "Certificado
    notarial - Constitución de SAS" directly beneath a header reading
    "Constitución de SAS" is the RFQ's name twice in two lines, and the half that
    distinguishes one child from its sibling — which trámite it is — gets pushed
    along by the half that does not.

    Ungrouped rows keep the suffix: there it is the ONLY thing saying the trámite
    came from a bespoke job.
  */
  if (nested) return label;
  return row.sourceRequestTitle ? `${label} - ${row.sourceRequestTitle}` : label;
}

/** A quote_request row has no catalog price — show the pro's own submitted bid once they've quoted, else a dash. */
function payLabelForRow(row: Pick<NotarialRequestListItem, "channel" | "myQuotePriceMinor" | "pricing">): string {
  if (row.channel === "quote") {
    return row.myQuotePriceMinor != null ? formatUyMinor(row.myQuotePriceMinor, "UYU") : "—";
  }
  return row.pricing ? formatUyMinor(row.pricing.totalProfessionalMinor, row.pricing.currency) : "—";
}

function StatusPill({
  row,
  tp,
}: {
  row: Pick<
    NotarialRequestListItem,
    | "status"
    | "fulfillmentStage"
    | "channel"
    | "myQuoteStatus"
    | "myClarificationStatus"
    | "quotingExpiresAt"
    | "pausedAt"
    | "isLocalToMe"
    | "deliveryAdminArea"
  >;
  tp: (notaryRelativePath: string) => string;
}): JSX.Element {
  if (row.channel === "quote") {
    const serverStatus = row.myQuoteStatus ?? "available";
    /*
      THE SERVER SAYS `available` ON A ROW NOBODY CAN BID ON ANY MORE, and it
      is right to: listAvailableForProfessional deliberately does not filter
      on quotingExpiresAt, because a consumer may still award past the
      deadline and hiding the row would erase a professional's own
      still-awardable bid (decision b960b6a7). `myQuoteStatus` is then null
      for anyone who never bid, which reads as `available`.

      The consequence was a badge reading "Disponible para cotizar" directly
      above a chip reading "El plazo cerró" — two statements contradicting
      each other, the badge being the wrong one, because submitOrUpdate
      refuses any submission past the deadline. The clock is already on the
      row; only the derivation was missing. mobile-pros has done this
      since its list shipped (effectiveMyQuoteStatus) and left a note that
      the portal had not.

      A bid that EXISTS is never rewritten: submitted / awarded / rejected
      all still mean what they say after the window shuts.
    */
    const expired =
      row.quotingExpiresAt != null &&
      new Date(row.quotingExpiresAt).getTime() <= Date.now();
    const myStatus =
      serverStatus === "available" && expired ? "missed" : serverStatus;
    // Only while the window still decides something. Once this pro's quote is
    // awarded or rejected the deadline is history, and a closed clock next to
    // "Adjudicada" reads as a warning about nothing.
    const showsDeadline = myStatus === "available" || myStatus === "submitted";
    // An answer from the client is the one thing on this row that is worth
    // coming back for, and the quote status cannot express it: a row whose
    // question was just answered reads "Disponible para cotizar", exactly like
    // a row nobody has touched.
    return (
      <div className="inline-flex flex-wrap items-center gap-1.5">
        <StatusBadge variant={QUOTE_MY_STATUS_VARIANTS[myStatus]}>
          {tp(`requests.quoteMyStatus_${myStatus}`)}
        </StatusBadge>
        {row.myClarificationStatus === "answered" ? (
          <StatusBadge variant="opportunity">
            {tp("requests.quoteClarificationAnswered")}
          </StatusBadge>
        ) : row.myClarificationStatus === "awaiting" ? (
          <StatusBadge variant="warning">
            {tp("requests.quoteClarificationAwaiting")}
          </StatusBadge>
        ) : null}
        {/* The ranking made visible. Local rows already sort first, but an
            order is not a statement — a professional cannot tell a deliberate
            ranking from an arbitrary one, and an unexplained reorder reads as
            the list being broken. The badge is also what lets someone in
            Rivera notice a Rivera job that is NOT flagged, which is how a
            wrong coverage match gets reported at all. */}
        {row.isLocalToMe && row.deliveryAdminArea ? (
          <StatusBadge variant="opportunity">
            {tp("requests.quoteLocalToYou")}
          </StatusBadge>
        ) : null}
        {showsDeadline && row.quotingExpiresAt ? (
          <QuoteDeadlineChip expiresAt={row.quotingExpiresAt} className="mt-0" />
        ) : null}
      </div>
    );
  }
  /*
    Paused rides ALONGSIDE the status rather than replacing it: pause is
    orthogonal to status by design, and a row that reads only "Pausada" loses
    where the work had got to — which is exactly what the professional comes
    back to this list for.
  */
  const pausedChip = row.pausedAt ? (
    <StatusBadge variant="warning">{tp("requests.pausedChip")}</StatusBadge>
  ) : null;
  if (row.fulfillmentStage) {
    return (
      <div className="inline-flex flex-wrap items-center gap-1.5">
        <StatusBadge variant="success">Completado</StatusBadge>
        <FulfillmentStageChip stage={row.fulfillmentStage} size="sm" />
        {pausedChip}
      </div>
    );
  }
  const status = row.status as NotarialRequestStatus;
  const opt = STATUS_OPTIONS.find((o) => o.value === status);
  const label = opt ? tp(opt.labelPath) : status;
  const statusBadge = (
    <StatusBadge variant={STATUS_VARIANTS[status] ?? "info"}>{label}</StatusBadge>
  );
  if (!pausedChip) return statusBadge;
  return (
    <div className="inline-flex flex-wrap items-center gap-1.5">
      {statusBadge}
      {pausedChip}
    </div>
  );
}

export function NotaryRequestsPage(): JSX.Element {
  const { t } = useTranslation();
  const { tp, tpo } = useProfessionalRequestCopy();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pagination = usePaginationFromUrl();

  const statusParamRaw = searchParams.get("status");
  const filterType = searchParams.get("type") ?? "";

  const selectedStatuses = useMemo<NotarialRequestStatus[]>(() => {
    if (statusParamRaw === null) return DEFAULT_STATUSES;
    if (statusParamRaw === "all" || statusParamRaw === "") return [];
    return statusParamRaw
      .split(",")
      .map((s) => s.trim())
      .filter((s): s is NotarialRequestStatus =>
        (SELECTABLE_STATUSES as string[]).includes(s),
      );
  }, [statusParamRaw]);

  const setSelectedStatuses = useCallback(
    (next: NotarialRequestStatus[]) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (next.length === 0) {
            p.set("status", "all");
          } else {
            p.set("status", next.join(","));
          }
          p.set("page", "1");
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const toggleStatus = useCallback(
    (status: NotarialRequestStatus, checked: boolean) => {
      const next = new Set(selectedStatuses);
      if (checked) next.add(status);
      else next.delete(status);
      setSelectedStatuses(Array.from(next));
    },
    [selectedStatuses, setSelectedStatuses],
  );

  const setFilterType = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set("type", value);
          else next.delete("type");
          next.set("page", "1");
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const listParams = useMemo(
    () => ({
      limit: pagination.limit,
      offset: (pagination.page - 1) * pagination.limit,
      ...(selectedStatuses.length > 0 && { statuses: selectedStatuses }),
      ...(filterType && { type: filterType }),
    }),
    [pagination.page, pagination.limit, selectedStatuses, filterType]
  );

  const {
    data,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["notary", "requests", listParams],
    queryFn: () => listNotaryRequests(listParams),
    // This list is a WORK QUEUE, and the professional leaves it open in a tab.
    // With focus refetching off, work that arrived while they were elsewhere —
    // a new request in the pool, or an RFQ they just won being spawned as a
    // real request — was invisible until they navigated or hit Actualizar. A
    // professional reporting "the client accepted my quote but I don't see the
    // request" is indistinguishable from a backend bug, and here it was the
    // stale tab: the API returns that row first, verified against dev.
    //
    // 30s staleTime so coming back to the tab refetches, while clicking
    // between filters and back does not re-issue the same query.
    refetchOnWindowFocus: true,
    staleTime: 30 * 1000,
  });

  useEffect(() => {
    if (data != null) {
      pagination.setTotal(data.total);
    } else if (isError) {
      pagination.setTotal(0);
    }
  }, [data, isError, pagination]);

  useEffect(() => {
    if (isError) {
      toast.error(tp("requests.error"));
    }
  }, [isError, tp]);

  const items: NotarialRequestListItem[] = data?.items ?? [];
  /*
    Derived at render rather than stored: `items` is the server's page and
    stays what pagination counts, so grouping must not become a second source
    of truth for what was loaded.
  */
  const groupedRows = groupProfessionalRowsBySourceRequest(items);
  const listBlocks = toProfessionalListBlocks(items);
  const loading = isLoading;
  const isNotaryPendingActivation =
    user?.role === "notary" &&
    user.isNotary &&
    user.notaryActivationStatus === "pending";
  const isAccountantPendingReview =
    (user?.role === "accountant" || user?.isAccountant === true) &&
    user.accountantActivationStatus === "pending";

  const rowActions = useCallback(
    (row: NotarialRequestListItem): TableAction[] => [
      {
        label: tp("requests.view"),
        icon: Eye,
        onClick: () => navigate(detailUrlForRow(row)),
      },
    ],
    [navigate, tp]
  );

  /*
    One card body, rendered for a standalone row and for a group member alike.
    A function rather than a component so it closes over `tp`/`rowActions`
    without a props object that would have to grow with every field the card
    shows — and so the two call sites cannot drift apart, which is exactly how
    the group member stopped looking like the row it is.
  */
  const renderRequestCard = (
    row: NotarialRequestListItem,
    grouped: boolean,
  ): JSX.Element => (
    <DataListCardRow onClick={() => navigate(detailUrlForRow(row))}>
      <div className="flex items-start justify-between gap-3">
        <p className="min-w-0 flex-1 font-semibold leading-snug text-foreground">
          {row.applicantName ?? "—"}
        </p>
        <div onClick={(e) => e.stopPropagation()} className="shrink-0">
          <TableActionsMenu actions={rowActions(row)} />
        </div>
      </div>
      <div className="mt-3 space-y-3">
        <DataListCardField label={tp("requests.type")}>
          <span className="text-muted-foreground">
            {typeLabelForRow(row, t, grouped)}
          </span>
        </DataListCardField>
        <div className="grid gap-3 sm:grid-cols-2">
          <DataListCardField label={tp("requests.status")}>
            <StatusPill row={row} tp={tp} />
          </DataListCardField>
          <DataListCardField label={tp("requests.columnYourPay")}>
            {payLabelForRow(row)}
          </DataListCardField>
          <DataListCardField label={tp("requests.createdAt")}>
            {formatDisplayDate(row.createdAt)}
          </DataListCardField>
        </div>
      </div>
    </DataListCardRow>
  );

  if (loading) {
    return (
      <div className="p-4 md:p-8">
        <div className="flex min-h-[40vh] items-center justify-center">
          <LoadingLogo size="lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      <PageHeader
        title={t("navigation.requests")}
        description={tp("requests.subtitle")}
      />
      {/* BEFORE the activation notices, because it is the one a professional can
          act on right now. "Wait for an admin" and "we need your RUT" are two
          separate assertions on the server on purpose, and a professional
          pending activation owes us this form either way. */}
      <FiscalDeclarationWarning />
      {isNotaryPendingActivation ? (
        <Alert
          variant="info"
          className="mt-4 transition-opacity duration-150 motion-reduce:transition-none"
        >
          <Info className="text-blue-600 dark:text-blue-400" aria-hidden />
          <AlertTitle>{tp("requests.pendingActivationTitle")}</AlertTitle>
          <AlertDescription className="text-blue-900/90 dark:text-blue-100/90">
            {tp("requests.pendingActivationDescription")}
          </AlertDescription>
        </Alert>
      ) : null}
      {isAccountantPendingReview ? (
        <Alert
          variant="info"
          className="mt-4 transition-opacity duration-150 motion-reduce:transition-none"
        >
          <Info className="text-blue-600 dark:text-blue-400" aria-hidden />
          <AlertTitle>{t("requests.user.accountantPendingReviewTitle")}</AlertTitle>
          <AlertDescription className="text-blue-900/90 dark:text-blue-100/90">
            {t("requests.user.accountantPendingReviewDescription")}
          </AlertDescription>
        </Alert>
      ) : null}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="w-[220px] justify-between">
              <span className="truncate">
                {selectedStatuses.length === 0
                  ? tp("requests.allStatuses")
                  : selectedStatuses.length === 1
                    ? tp(
                        STATUS_OPTIONS.find((o) => o.value === selectedStatuses[0])
                          ?.labelPath ?? "requests.status",
                      )
                    : tpo("requests.statusesSelected", { count: selectedStatuses.length })}
              </span>
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-60" aria-hidden />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[260px]">
            <DropdownMenuLabel>{tp("requests.status")}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {SELECTABLE_STATUSES.map((value) => {
              const opt = STATUS_OPTIONS.find((o) => o.value === value)!;
              return (
                <DropdownMenuCheckboxItem
                  key={value}
                  checked={selectedStatuses.includes(value)}
                  onCheckedChange={(checked) => toggleStatus(value, Boolean(checked))}
                  onSelect={(e) => e.preventDefault()}
                >
                  {tp(opt.labelPath)}
                </DropdownMenuCheckboxItem>
              );
            })}
            <DropdownMenuSeparator />
            <button
              type="button"
              className="w-full px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground rounded-sm"
              onClick={() => setSelectedStatuses([])}
            >
              {tp("requests.allStatuses")}
            </button>
          </DropdownMenuContent>
        </DropdownMenu>
        <Select
          value={filterType || "all"}
          onValueChange={(v) => setFilterType(v === "all" ? "" : v)}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder={tp("requests.type")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{tp("requests.allTypes")}</SelectItem>
            {REQUEST_TYPE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {t(opt.labelKey)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <TableCard className="mt-4">
        {items.length === 0 ? (
          <div className="flex min-h-[40vh] items-center justify-center">
            <EmptyState
              title={tp("requests.emptyTitle")}
              message={tp("requests.emptyMessage")}
            />
          </div>
        ) : (
          <>
            <ResponsiveListSplit
              table={
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{tp("requests.applicantName")}</TableHead>
                      <TableHead>{tp("requests.type")}</TableHead>
                      <TableHead>{tp("requests.status")}</TableHead>
                      <TableHead>{tp("requests.columnYourPay")}</TableHead>
                      <TableHead>{tp("requests.createdAt")}</TableHead>
                      <TableHead className="w-[80px]">{tp("requests.actions")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {groupedRows.map((entry) => {
                      const { row, groupTitle, grouped, groupSize } = entry;
                      /*
                        NO SPACER BETWEEN BLOCKS, and that is the point: every
                        row in this table is the same height, one divider
                        apart. Two versions of this tried to set a group off
                        with whitespace — blank first, then a tinted seam — and
                        both read as a row that had been pushed away from the
                        list for a reason nobody could name. What marks the
                        group is the tint, the rail and the header; the rhythm
                        of the table is not a thing to spend on saying it
                        again.
                      */
                      /*
                        The parent RFQ's own detail. Null only if a grouped row
                        ever arrives without the id it was grouped BY, in which
                        case the block silently loses its header rather than
                        offering a link to nowhere.
                      */
                      const parentUrl =
                        row.sourceRequestId != null
                          ? `/requests/${row.sourceRequestId}?channel=quote`
                          : null;
                      return (
                        <Fragment key={row.id}>
                          {groupTitle != null && parentUrl != null ? (
                            /*
                              A LINK, and a real <a>. The parent RFQ leaves the
                              professional's LIST at payment — the same event
                              that spawns these children — but it does not stop
                              existing: the pro detail endpoint still serves it
                              to anyone holding a leg of their discipline, which
                              is by construction everyone who can see this
                              header. It is where the scope, the attachments and
                              their own awarded bid live, so the one row naming
                              the job is the row that should open it.

                              THE ANCHOR FILLS THE CELL rather than wrapping the
                              title alone: the row-sized target is what the rest
                              of this table trained the hand to expect, and an
                              anchor gives it the keyboard focus, the status-bar
                              URL and the middle-click that an onClick on a <tr>
                              never has.

                              IT SITS ONE STEP IN AND ITS MEMBERS SIT TWO.
                              The header starts at `pl-7`, inside the standalone
                              rows' own column; the trámites under it start at
                              `pl-12`. The step between them is what says
                              "these belong to that" without a word — the
                              version where header and members shared one edge
                              read as a banner with rows merely stacked under
                              it.

                              ONLY THE HEADER IS TINTED, and only at 4.5%.
                              `primary` is a near-black navy, so what reads as
                              "a light grey" here is a single-digit alpha — at
                              8% the band was heavier than the table's own
                              thead. The members carry NO fill at all: a wash
                              that light is invisible on half a screen and
                              muddy on the other half, and it was buying a
                              boundary the rail already draws. What says
                              "these rows belong to that header" is the rail
                              and the indent, both of which hold at any
                              brightness.

                              THE RAIL IS A CELL BORDER, not a box-shadow or a
                              rounded wrapper: Tailwind's preflight sets
                              `border-collapse: collapse` on every table, under
                              which border-radius does not paint and borders on
                              <tr> are dropped. A left border on the FIRST cell
                              of each row in the group is the one thing that
                              paints, and it lines up into one continuous rail
                              down the whole block.
                            */
                            <TableRow className="border-b-primary/15 bg-primary/[0.045] hover:bg-primary/[0.045] dark:border-b-primary/25 dark:bg-primary/[0.10] dark:hover:bg-primary/[0.10]">
                              <TableCell
                                colSpan={6}
                                className="border-l-4 border-primary/60 p-0"
                              >
                                <Link
                                  to={parentUrl}
                                  className="group/parent flex flex-wrap items-center gap-x-2 gap-y-1 py-2.5 pl-7 pr-4 transition-colors hover:bg-primary/[0.07] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset dark:hover:bg-primary/10"
                                >
                                  <Layers
                                    className="h-3.5 w-3.5 shrink-0 text-primary/60"
                                    aria-hidden
                                  />
                                  <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                    {tp("requests.partOf")}
                                  </span>
                                  <span className="text-sm font-semibold text-foreground underline-offset-4 group-hover/parent:underline">
                                    {groupTitle}
                                  </span>
                                  {/* A CHIP WITH AN EDGE. On a band this pale
                                      a white pill is the band again: the count
                                      was there and nobody saw it. Tinted and
                                      bordered in the rail's own colour, it
                                      reads as the one piece of data in a row
                                      of labels — which is what it is, the
                                      answer to "how much of this job is
                                      mine". */}
                                  <span className="rounded-full border border-primary/20 bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary dark:border-primary/40 dark:bg-primary/20 dark:text-foreground">
                                    {tpo("requests.partOfCount", { count: groupSize })}
                                  </span>
                                  {/* THE AFFORDANCE SITS WHERE THE ROW'S
                                      CONTROL SITS — pushed to the far right,
                                      under the actions column, so the header
                                      reads left-to-right like every data row
                                      below it instead of trailing a loose
                                      glyph after the pill. It nudges on hover
                                      and the title underlines; neither CHANGES
                                      COLOUR, because a header someone is only
                                      reading should not look half-pressed. */}
                                  <ChevronRight
                                    className="ml-auto h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover/parent:translate-x-0.5"
                                    aria-hidden
                                  />
                                </Link>
                              </TableCell>
                            </TableRow>
                          ) : null}
                          <TableRow
                            /*
                              A member differs from a standalone row by its
                              rail and its indent and NOTHING else — same
                              height, same divider, same hover. The block ends
                              where the rail stops.
                            */
                            className="cursor-pointer transition-colors"
                            onClick={() => navigate(detailUrlForRow(row))}
                          >
                            <TableCell
                              className={`font-medium ${
                                grouped
                                  ? "border-l-4 border-primary/60 pl-12"
                                  : ""
                              }`}
                            >
                              {row.applicantName ?? "—"}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {typeLabelForRow(row, t, grouped)}
                            </TableCell>
                            <TableCell>
                              <StatusPill row={row} tp={tp} />
                            </TableCell>
                            <TableCell className="text-sm text-foreground">
                              {payLabelForRow(row)}
                            </TableCell>
                            <TableCell>{formatDisplayDate(row.createdAt)}</TableCell>
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              <TableActionsMenu actions={rowActions(row)} />
                            </TableCell>
                          </TableRow>
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              }
              cards={
                /*
                  THE CARD LIST DRAWS A REAL BLOCK, not the banner-plus-indent
                  the table is forced into: here a group is one bordered,
                  rounded container wrapping its header and its members, with
                  whitespace between blocks, which is the shape the table can
                  only approximate with a rail and a tint.
                */
                <div className="flex flex-col gap-3 p-3">
                  {listBlocks.map((block) =>
                    block.kind === "single" ? (
                      <div
                        key={block.key}
                        className="overflow-hidden rounded-xl border border-border/70"
                      >
                        {renderRequestCard(block.row, false)}
                      </div>
                    ) : (
                      <div
                        key={block.key}
                        className="overflow-hidden rounded-xl border border-primary/25 bg-primary/[0.02] dark:border-primary/30 dark:bg-primary/[0.045]"
                      >
                        {block.title ? (
                          /* Not a DataListCardRow but a link all the same: the
                             block's key IS the parent RFQ's id, and the same
                             detail the table's header opens is what a thumb
                             expects from the only row naming the job. */
                          <Link
                            to={`/requests/${block.key}?channel=quote`}
                            className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-primary/15 bg-primary/[0.045] px-3 py-2 transition-colors active:bg-primary/[0.09] dark:border-primary/25 dark:bg-primary/[0.10]"
                          >
                            <Layers
                              className="h-3.5 w-3.5 shrink-0 text-primary/60"
                              aria-hidden
                            />
                            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                              {tp("requests.partOf")}
                            </span>
                            <span className="text-sm font-semibold text-foreground">
                              {block.title}
                            </span>
                            <span className="rounded-full border border-primary/20 bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary dark:border-primary/40 dark:bg-primary/20 dark:text-foreground">
                              {tpo("requests.partOfCount", {
                                count: block.rows.length,
                              })}
                            </span>
                            <ChevronRight
                              className="ml-auto h-4 w-4 shrink-0 text-muted-foreground"
                              aria-hidden
                            />
                          </Link>
                        ) : null}
                        <div className="divide-y divide-primary/15">
                          {block.rows.map((row) => (
                            <Fragment key={row.id}>
                              {renderRequestCard(row, true)}
                            </Fragment>
                          ))}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              }
            />
            <TablePagination
              pagination={pagination}
              rowsPerPageLabel={tp("requests.rowsPerPage")}
              pageOfLabel={tpo("requests.pageOf", {
                from: (pagination.page - 1) * pagination.limit + 1,
                to: Math.min(
                  pagination.page * pagination.limit,
                  pagination.total
                ),
                total: pagination.total,
              })}
              onRefresh={() => void refetch()}
              isRefreshing={isFetching}
            />
          </>
        )}
      </TableCard>
    </div>
  );
}
